import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'

import { TrialStatus } from '@/reducers/auth/userReducer'
import { getFetchError, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import {
  FETCH_STRIPE_PRODUCTS_KEY,
  StripeProduct,
  fetchEligibleStripeProducts,
  PlanTier,
} from '@/actions/settings/billingActions'
import {
  getCurrentUser,
  getFreeTrialStatus,
  selectFreeTrialLengthDays,
} from '@/selectors/user.selectors'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { useSignupPageUpdate } from '@/services/onboardingService'
import {
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsView } from '@/features/amplitude'

import {
  FREE_TRIAL_PROMO,
  SIGNUP_PATHS,
  fetchFreeTrialLength,
} from '../helpers'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import { useSignupCheckout } from '../checkout/useSignupCheckout'
import { TextColors, TypographyStyles } from '@/styles'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'
import {
  Alert,
  Button,
  Card,
  CardColors,
  Checkbox,
  Separator,
  Text,
  Loader,
} from '@/ui'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'
import { StripeProductCard } from './StripeProductCard'
import { selectCanOptIntoPreviousTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { HEARD_LITE_PRODUCT_IDS } from '@/constants/pricingConstants'

export const LegacyPayment = ({ isUpsell }: { isUpsell?: boolean }) => {
  const navigate = useNavigateWithPersistParams()
  const dispatch = useAppDispatch()
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>()
  const [annualCommitmentConfirmation, setAnnualCommitmentConfirmation] =
    useState(false)
  const user = useReselector(getCurrentUser)
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, false)
  const [selectedProduct, setSelectedProduct] = useState<StripeProduct>()
  const [searchParams] = useSearchParams()
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )

  const { submit, loading, checkoutError, useTwoCheckoutFlow } =
    useSignupCheckout({
      selectedProduct,
      successUrl: SIGNUP_PATHS.success,
      screenName: SIGNUP_PATHS.payment,
      isUpsell,
    })
  const selectedPlanTier = localStorage.getItem('planTier')
  const selectedLitePlan = selectedPlanTier === PlanTier.lite

  /* Free-trial related */
  // e.g. active, inactive, unredeemed
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)

  // Based on user's current & past activity, should we enable the trial
  const freeTrialSearchParamValue = searchParams.get('trial')
  const freeTrialEnabled = useFreeTrialEnabled(freeTrialSearchParamValue)
  useSignupPageUpdate(SIGNUP_PATHS.payment, isUpsell)

  const pageView = useAnalyticsView()

  useEffect(() => {
    if (isUpsell) {
      pageView('upsell flow choose plan')
    } else {
      pageView('sign up choose plan')
    }
  }, [pageView, isUpsell])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useEffect(() => {
    if (checkoutError) {
      scrollToRef()
    }
  }, [checkoutError, scrollToRef])

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
  }, [dispatch])

  const fetchingStripeProducts = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_STRIPE_PRODUCTS_KEY
  )

  const fetchingStripeProductsError = useReselector(
    getFetchError,
    FETCH_STRIPE_PRODUCTS_KEY
  )

  useEffect(() => {
    async function performAsync() {
      const products = await fetchEligibleStripeProducts()(dispatch)
      let data: StripeProduct[] | undefined

      // Identify Lite products by either metadata.tier OR product ID — staging
      // products may temporarily carry tier='starter' until the API enum
      // change is deployed. Falling back to HEARD_LITE_PRODUCT_IDS keeps the
      // filter stable through that transition.
      const isLiteProduct = (p: StripeProduct) =>
        p.metadata.tier === PlanTier.lite ||
        HEARD_LITE_PRODUCT_IDS.includes(p.id)

      if (selectedLitePlan) {
        data = products?.filter(isLiteProduct)
      } else {
        data = products?.filter(
          (p) => p.metadata.tier !== PlanTier.starter && !isLiteProduct(p)
        )
      }

      // Sort annual products first (currently we only expect 2 products - monthly & annual)
      if (data && data.length > 1) {
        data.sort(
          (a, b) =>
            -a.default_price.recurring.interval.localeCompare(
              b.default_price.recurring.interval
            )
        )
      }
      setStripeProducts(data)

      // Auto-select the first product only if the user hasn't already chosen
      // a plan in the choose-plan step.
      if (!selectedPlanTier && data && data.length > 0) {
        setSelectedProduct(data[0])
      }
    }

    performAsync()
  }, [
    dispatch,
    user?.financialProfile?.practiceType,
    selectedLitePlan,
    selectedPlanTier,
  ])

  // Heard Lite is only offered to solo, non-S-corp practices. Group practices
  // and S-corps never see the choose-plan (Lite vs Essential) step, so back
  // should skip it for them too.
  const isScorp =
    user?.financialProfile?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s ||
    user?.financialProfile?.taxEntityType === TAX_ENTITY_TYPES.form_1120
  const isGroup = user?.financialProfile?.practiceType === 'group'
  const userBypassedChoosePlan = isScorp || isGroup

  const handleBackButton = () => {
    if (userBypassedChoosePlan) {
      // When skipping choose plan, still respect the tax year logic
      navigate(
        canOptIntoPreviousYearTaxes &&
          freeTrialSearchParamValue !== FREE_TRIAL_PROMO &&
          !selectedLitePlan
          ? SIGNUP_PATHS.previousYearTax
          : SIGNUP_PATHS.aboutYourPractice
      )
    } else {
      navigate(
        canOptIntoPreviousYearTaxes &&
          freeTrialSearchParamValue !== FREE_TRIAL_PROMO &&
          !selectedLitePlan
          ? SIGNUP_PATHS.previousYearTax
          : SIGNUP_PATHS.choosePlan
      )
    }
  }

  /** Calculate Free Trial Days based on referral date */
  const freeTrialDays = fetchFreeTrialLength(freeTrialSearchParamValue)
  // An ended trial has no `?trial=` param to read, so take its length from the
  // membership instead, and omit the count entirely if the code is unknown.
  const endedTrialLength = useReselector(selectFreeTrialLengthDays)
  const trialEndDate = moment()
    .add(freeTrialDays, 'days')
    .format(DATE_FORMATS.DISPLAY_SIMPLE)

  const planSubText = useMemo(() => {
    if (freeTrialEnabled && !isUpsell) {
      return `Try Heard for the first ${freeTrialDays} days before getting billed. You won't be charged until your trial ends on ${trialEndDate}. Cancel anytime.`
    } else {
      if (hasAnnualCommitment) {
        return "Based on your responses, we've selected the best plan for your practice. Please note that all plans require a 12-month commitment, regardless of monthly or annual payment choice."
      } else {
        return 'Based on your answers, the plan below fits your practice.'
      }
    }
  }, [
    freeTrialEnabled,
    freeTrialDays,
    isUpsell,
    hasAnnualCommitment,
    trialEndDate,
  ])

  const headerTitle = isUpsell
    ? 'Ready to unlock the full experience?'
    : 'Choose to pay annually or monthly, whatever meets your needs.'
  const continueDisabled =
    loading || (hasAnnualCommitment && !annualCommitmentConfirmation)

  /* No products found - in the event products aren't properly configured on Stripe.com */
  const noStripeProductsAvailable =
    !fetchingStripeProducts && !fetchingStripeProductsError && !stripeProducts

  const content = (
    <div className="flex flex-col gap-4">
      <span ref={scrollRef} />

      <div className="w-full">
        <Text variant={TypographyStyles.DISPLAY_MD}>{headerTitle}</Text>

        {isUpsell && (
          <Text variant={TypographyStyles.BODY_LG}>
            Join thousands of {replacementText} entrepreneurs who trust Heard to
            handle their finances. By purchasing Heard, you will gain full
            access to bookkeeping services, tax support, and financial tools
            designed specifically for your practice.
          </Text>
        )}
      </div>

      {userFreeTrialStatus === TrialStatus.inactive && (
        <Card color={CardColors.WARNING}>
          <Text>
            Your {endedTrialLength ? `${endedTrialLength}-day ` : ''}free trial
            has ended. Please choose a plan and complete payment to access your
            heard account.
          </Text>
        </Card>
      )}

      {userFreeTrialStatus !== TrialStatus.inactive && !isUpsell && (
        <Text
          className="w-full min-h-fit !mb-8"
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
        >
          {planSubText}
        </Text>
      )}

      {checkoutError && <Alert variant="error">{checkoutError}</Alert>}

      <div className="w-full">
        <Loader active={fetchingStripeProducts} />

        {fetchingStripeProductsError && (
          <Card color={CardColors.LEVEL_0}>
            <Text variant={TypographyStyles.H2}>Missing information</Text>
            <Text>{fetchingStripeProductsError.message}</Text>
          </Card>
        )}

        {!fetchingStripeProducts && stripeProducts && (
          <StripeProductCard
            stripeProducts={stripeProducts}
            selectedProduct={selectedProduct}
            onSelect={setSelectedProduct}
            freeTrialEnabled={freeTrialEnabled}
            freeTrialSearchParamValue={freeTrialSearchParamValue}
            useTwoCheckoutFlow={useTwoCheckoutFlow}
          />
        )}

        {noStripeProductsAvailable && (
          <Text>
            We could not find any matching Products for you. Please email
            contact@joinheard.com for further assistance.
          </Text>
        )}
      </div>

      {hasAnnualCommitment && (
        <Card color={CardColors.LEVEL_0}>
          <Checkbox
            label="By checking this box, I understand I'm committing to a 12-month membership with Heard, payable either upfront (Annual Payment Plan) or monthly (Monthly Payment Plan)."
            checked={annualCommitmentConfirmation}
            onCheckedChange={(checked: boolean) =>
              setAnnualCommitmentConfirmation(checked)
            }
          />
        </Card>
      )}
    </div>
  )

  if (isUpsell) {
    return (
      <div className="container mx-auto pt-16 mb-4">
        <div className="flex flex-col gap-4">
          {content}

          <Separator className="my-6" />

          <div className="flex justify-end">
            {selectedProduct && (
              <Button
                onClick={submit}
                disabled={continueDisabled}
                loading={loading}
              >
                Purchase
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onBack={handleBackButton}
          onContinue={submit}
          continueDisabled={continueDisabled || !selectedProduct}
          continueLoading={loading}
          continueId="btn-signup-payment"
        />
      }
    >
      {content}
    </SignupPageLayout>
  )
}
